import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../Button';
import { Checkbox } from '../../form/Checkbox';
import { toast } from 'react-toastify';
import Ai from '../../../store/models/Ai';
import Tooltip from '../../Tooltip';
import { InfoIcon, CopyIcon } from '../../Svg';
import _ from 'lodash';
import ChatbotTools, { createAgentEscalationToolBody } from '../../../store/models/RagChatbot/ChatbotTools';
import { selectCurrentUser } from '../../../store/models/User';
import { getTalkSession } from '@talkjs/core';

export const TalkJSConfiguration = ({ chatbot, projectId, onConfigurationSaved, bypassLlmCheck = false }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);

  // Form state for TalkJS configuration
  const [formAppId, setFormAppId] = useState(_.get(chatbot, 'ui.talkjs.app_id', ''));
  const [formSecretKey, setFormSecretKey] = useState(_.get(chatbot, 'ui.talkjs.secret_key', ''));
  const [formEnabled, setFormEnabled] = useState(_.get(chatbot, 'ui.talkjs.enabled', false));
  const [formAdminEmail, setFormAdminEmail] = useState(_.get(chatbot, 'ui.talkjs.admin_email', '') || currentUser?.email || '');
  const [saving, setSaving] = useState(false);
  
  // Email template URL fields (read-only, auto-generated)
  const formEmailTemplateAdminRedirectUrl = `https://www.thetaedgecloud.com/dashboard/ai/${projectId}/rag-chatbot-detail/${chatbot.id}/tab/chats?conversation={{conversation.id}}`;
  const formEmailTemplateDefaultRedirectUrl = `https://integrate.thetaedgecloud.com/chatbot/${chatbot.id}?conversation={{conversation.id}}&user={{recipient.id}}`;

  // Saved configuration values
  const savedConfig = {
    appId: _.get(chatbot, 'ui.talkjs.app_id', ''),
    secretKey: _.get(chatbot, 'ui.talkjs.secret_key', ''),
    enabled: _.get(chatbot, 'ui.talkjs.enabled', false),
    adminEmail: _.get(chatbot, 'ui.talkjs.admin_email', '') || currentUser?.email || ''
  };

  // Check if the current LLM endpoint supports advanced features
  const supportsAdvancedFeatures = React.useMemo(() => {
    // If bypassLlmCheck is true, always return true (for mini dashboard)
    if (bypassLlmCheck) {
      return true;
    }
    
    const endpointId = _.get(chatbot, 'ui.llm_endpoint_id', '');
    const llmEndpoints = _.get(chatbot, 'llm_endpoints', []);
    const endpoint = llmEndpoints.find(e => e.id === endpointId);
    const endpointName = _.get(endpoint, 'name', '').toLowerCase();

    return endpointName.includes('gpt') || endpointName.includes('claude');
  }, [chatbot, bypassLlmCheck]);

  // Update form state when chatbot prop changes
  useEffect(() => {
    setFormAppId(savedConfig.appId);
    setFormSecretKey(savedConfig.secretKey);
    setFormEnabled(savedConfig.enabled);
    setFormAdminEmail(savedConfig.adminEmail);
  }, [savedConfig.appId, savedConfig.secretKey, savedConfig.enabled, savedConfig.adminEmail]);

  // Reset TalkJS when endpoint changes and doesn't support advanced features
  useEffect(() => {
    if (!supportsAdvancedFeatures && savedConfig.enabled) {
      updateTalkjsSettings({ enabled: false });
      toast.info("TalkJS chat support disabled: current LLM endpoint only supports basic features");
    }
  }, [supportsAdvancedFeatures, savedConfig.enabled]);

  // Helper function to update TalkJS settings
  const updateTalkjsSettings = async (settings) => {
    try {
      const updatedChatbot = {
        ...chatbot,
        ui: {
          ...chatbot.ui,
          talkjs: {
            ..._.get(chatbot, 'ui.talkjs', {}),
            ...settings
          }
        }
      };

      await dispatch(Ai.actions.updateRagChatbot(chatbot.id, updatedChatbot));
    } catch (error) {
      console.error('Failed to update TalkJS settings:', error);
    }
  };

  // Create agent escalation tool
  const createAgentEscalationTool = async (currentChatbot = chatbot, appId = null, secretKey = null) => {
    const useAppId = appId || savedConfig.appId;
    const useSecretKey = secretKey || savedConfig.secretKey;
    if (!useAppId || !useSecretKey) return;

    const toolBody = createAgentEscalationToolBody(projectId, chatbot.id);

    try {
      const result = await dispatch(ChatbotTools.actions.createChatbotTool(projectId, chatbot.id, toolBody));
      console.log('Tool creation result:', result);

      if (result && result.id) {
        // Store the tool ID in chatbot UI settings for future reference
        const updatedChatbot = {
          ...currentChatbot,
          ui: {
            ...currentChatbot.ui,
            talkjs: {
              ..._.get(currentChatbot, 'ui.talkjs', {}),
              escalation_tool_id: result.id
            }
          }
        };

        console.log('Storing tool ID:', result.id, 'in chatbot settings');
        dispatch(Ai.actions.updateRagChatbot(chatbot.id, updatedChatbot));
        toast.success('Agent escalation tool created successfully!');
      } else {
        console.error('Tool creation succeeded but no ID returned:', result);
        toast.error('Tool created but ID not returned');
      }
    } catch (error) {
      console.error('Error creating agent escalation tool:', error);
      toast.error('Failed to create agent escalation tool');
    }
  };

  // Delete agent escalation tool
  const deleteAgentEscalationTool = async (currentChatbot = chatbot) => {
    const toolId = _.get(currentChatbot, 'ui.talkjs.escalation_tool_id');
    console.log('Attempting to delete escalation tool with ID:', toolId);
    console.log('Current chatbot ui.talkjs:', _.get(currentChatbot, 'ui.talkjs', {}));

    // Always clean up the tool ID, even if deletion fails
    const cleanupToolId = async () => {
      const updatedChatbotForCleanup = {
        ...currentChatbot,
        ui: {
          ...currentChatbot.ui,
          talkjs: {
            ..._.get(currentChatbot, 'ui.talkjs', {}),
            escalation_tool_id: null
          }
        }
      };

      await dispatch(Ai.actions.updateRagChatbot(currentChatbot.id, updatedChatbotForCleanup));
    };

    try {
      let toolToDelete = null;

      if (toolId) {
        // Use stored tool ID if available
        toolToDelete = { id: toolId };
      } else {
        // Search for escalation tool by name if no ID stored
        console.log('No stored tool ID, searching for escalation tool by name...');
        console.log('Searching in project:', projectId, 'chatbot:', currentChatbot.id);
        const result = await dispatch(ChatbotTools.actions.getChatbotTools(projectId, currentChatbot.id, 0, 100));
        console.log('Found tools:', result.items.map(t => ({ id: t.id, name: t.name })));

        // Try exact match first
        toolToDelete = result.items.find(tool => tool.name === 'Escalate to Human Agent');

        // If no exact match, try partial matches for escalation-related tools
        if (!toolToDelete) {
          toolToDelete = result.items.find(tool =>
            tool.name.toLowerCase().includes('escalat') ||
            tool.name.toLowerCase().includes('human') ||
            tool.name.toLowerCase().includes('agent') ||
            tool.description?.toLowerCase().includes('escalat')
          );
          if (toolToDelete) {
            console.log('Found escalation tool by partial match:', toolToDelete.id, toolToDelete.name);
          }
        } else {
          console.log('Found escalation tool by exact name match:', toolToDelete.id);
        }
      }

      if (toolToDelete) {
        console.log('Deleting tool with ID:', toolToDelete.id);
        try {
          await dispatch(ChatbotTools.actions.deleteChatbotTool(projectId, currentChatbot.id, toolToDelete.id));
          console.log('Tool deletion completed');
          toast.success('Agent escalation tool removed successfully!');
        } catch (e) {
          console.log(e.message)
        }

        await cleanupToolId();
      } else {
        console.log('No tool found to delete');
        // No tool found, just clean up any stored ID
        await cleanupToolId();
      }
    } catch (error) {
      console.error('Error deleting agent escalation tool:', error);
      // Still clean up the tool ID even if deletion failed
      await cleanupToolId();
      toast.error('Failed to remove agent escalation tool, but cleaned up references');
    }
  };

  // Copy handlers for email template URLs
  const handleCopyAdminUrl = () => {
    navigator.clipboard.writeText(formEmailTemplateAdminRedirectUrl)
      .then(() => {
        toast.success('Admin Redirect URL Copied!')
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  const handleCopyDefaultUrl = () => {
    navigator.clipboard.writeText(formEmailTemplateDefaultRedirectUrl)
      .then(() => {
        toast.success('Default Redirect URL Copied!')
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  // Validate admin role exists in TalkJS
  const validateAdminRole = async (appId, secretKey, adminEmail) => {
    try {
      // Generate JWT token for testing
      const token = await generateTalkJSToken('test_admin_role', appId, secretKey);
      
      // Try to create a test user with admin role
      const testSession = getTalkSession({
        appId: appId,
        userId: 'test_admin_role',
        token: token
      });
      
      const userRef = testSession.user('test_admin_role');
      await userRef.createIfNotExists({
        name: 'Test Admin Role Validation',
        email: adminEmail,
        role: 'admin'
      });
      
      // Note: @talkjs/core sessions don't need explicit destruction
      // Session cleanup is handled automatically
      
      return { success: true };
    } catch (error) {
      console.error('Admin role validation failed:', error);
      
      // Check if error is related to role not existing
      if (error.message && (
        error.message.toLowerCase().includes('role') ||
        error.message.toLowerCase().includes('admin') ||
        error.message.toLowerCase().includes('unknown') ||
        error.message.toLowerCase().includes('invalid')
      )) {
        return { 
          success: false, 
          error: 'Admin role not found in TalkJS dashboard. Please create an "admin" role in your TalkJS dashboard first.' 
        };
      }
      
      return { 
        success: false, 
        error: `TalkJS configuration validation failed: ${error.message}` 
      };
    }
  };

  // Generate JWT token for TalkJS authentication
  const generateTalkJSToken = async (userId, appId, secretKey) => {
    const { SignJWT } = await import('jose');
    const secret = new TextEncoder().encode(secretKey);
    
    const jwt = await new SignJWT({
      tokenType: 'user',
      iss: appId,
      sub: userId,
    })
    .setProtectedHeader({ alg: 'HS256' })
    .sign(secret);
    
    return jwt;
  };

  // Save TalkJS configuration
  const handleSaveTalkjsConfig = async () => {
    if (!supportsAdvancedFeatures && formEnabled) {
      toast.warning("TalkJS chat support requires GPT or Claude endpoint");
      return;
    }

    if (formEnabled && (!formAppId || !formSecretKey || !formAdminEmail)) {
      toast.error("Please fill in all required fields (App ID, Secret Key, and Admin Email)");
      return;
    }

    setSaving(true);

    try {
      // First, validate that the admin role exists if TalkJS is being enabled
      if (formEnabled) {
        toast.info("Validating TalkJS configuration...");
        const validation = await validateAdminRole(formAppId, formSecretKey, formAdminEmail);
        
        if (!validation.success) {
          toast.error(validation.error);
          setSaving(false);
          return;
        }
        
        toast.success("TalkJS configuration validated successfully!");
      }
      const updatedChatbot = {
        ...chatbot,
        ui: {
          ...chatbot.ui,
          talkjs: {
            ..._.get(chatbot, 'ui.talkjs', {}),
            app_id: formAppId,
            secret_key: formSecretKey,
            enabled: formEnabled,
            admin_email: formAdminEmail
          }
        }
      };

      dispatch(Ai.actions.updateRagChatbot(chatbot.id, updatedChatbot));
      dispatch(Ai.actions.fetchRagChatbot(projectId, chatbot.id));

      // Create or delete agent escalation tool based on enabled state
      if (formEnabled && formAppId && formSecretKey) {
        const existingToolId = _.get(chatbot, 'ui.talkjs.escalation_tool_id');

        // Only create tool if it doesn't already exist
        if (!existingToolId) {
          await createAgentEscalationTool(updatedChatbot, formAppId, formSecretKey);
        }

        // Notify parent component that configuration was saved
        if (onConfigurationSaved) {
          onConfigurationSaved(formAppId, formSecretKey);
        }
      } else if (!formEnabled) {
        // Delete the escalation tool when TalkJS is disabled
        await deleteAgentEscalationTool(updatedChatbot);
      }

      toast.success('TalkJS configuration saved successfully!');
    } catch (error) {
      console.error('Error saving TalkJS config:', error);
      toast.error('Failed to save TalkJS configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className='RagChatbotDetailPage__section-subtitle'>
        <a 
          href="https://docs.google.com/document/d/1j1MRQhpcLj804WYPG_Xo3Cbf3XsqiB1uZZyvo_EHV3A/edit?usp=sharing" 
          target="_blank" 
          rel="noopener noreferrer"
          className='RagChatbotDetailPage__docs-link'
        >
          <Tooltip tooltip={"View TalkJS configuration documentation"}>
            <InfoIcon />
          </Tooltip>
          How to setup Realtime Support with TalkJS
        </a>
      </div>
      <div className='RagChatbotDetailPage__section-content'>

        <div className='RagChatbotDetailPage__row'>
          <div className='RagChatbotDetailPage__input-wrap'>
            <div className='RagChatbotDetailPage__input-label'>
              Customer Chat Support
              <Tooltip tooltip={"Enable TalkJS integration to allow customers to escalate to human support agents"}>
                <InfoIcon />
              </Tooltip>
            </div>
            <Checkbox
              label="Enable TalkJS Chat Support"
              checked={formEnabled}
              onChange={() => setFormEnabled(!formEnabled)}
              disabled={!supportsAdvancedFeatures}
            />
            {!supportsAdvancedFeatures && (
              <div className="RagChatbotDetailPage__feature-disabled-notice">
                Requires ChatGPT or Claude LLM endpoint
              </div>
            )}
          </div>
        </div>

        {formEnabled && (
          <>
            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>
                  TalkJS App ID
                  <Tooltip tooltip={"Your TalkJS Application ID from the TalkJS dashboard (starts with 'tA')"}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <input
                  className='RagChatbotDetailPage__input'
                  type="text"
                  placeholder="tA1B2C3D"
                  value={formAppId}
                  onChange={(e) => setFormAppId(e.target.value)}
                />
              </div>
            </div>

            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>
                  TalkJS Secret Key
                  <Tooltip tooltip={"Your TalkJS Secret Key from the TalkJS dashboard (starts with 'sk_')"}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <input
                  className='RagChatbotDetailPage__input'
                  type="password"
                  placeholder="sk_test_..."
                  value={formSecretKey}
                  onChange={(e) => setFormSecretKey(e.target.value)}
                />
              </div>
            </div>

            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>
                  Admin Email Address
                  <Tooltip tooltip={"Email address for the admin user that will be created in TalkJS with admin role"}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <input
                  className='RagChatbotDetailPage__input'
                  type="email"
                  placeholder="admin@example.com"
                  value={formAdminEmail}
                  onChange={(e) => setFormAdminEmail(e.target.value)}
                />
                <div className="RagChatbotDetailPage__info-notice">
                  <strong>Note:</strong> Make sure to create an "admin" role in your TalkJS dashboard before enabling this feature. 
                </div>
              </div>
            </div>

            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>
                  Email Template Admin Redirect URL
                  <Tooltip tooltip={"Used for email notification configuration, see documentation"}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <div className='RagChatbotDetailPage__input-div token'>
                  <div className='RagChatbotDetailPage__input-div--info'>{formEmailTemplateAdminRedirectUrl}</div>
                  <div className='RagChatbotDetailPage__input-div--buttons'>
                    <div className='RagChatbotDetailPage__input-div--icon' onClick={handleCopyAdminUrl}>
                      <CopyIcon />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='RagChatbotDetailPage__row'>
              <div className='RagChatbotDetailPage__input-wrap'>
                <div className='RagChatbotDetailPage__input-label'>
                  Email Template Default Redirect URL
                  <Tooltip tooltip={"Used for email notification configuration, see documentation"}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <div className='RagChatbotDetailPage__input-div token'>
                  <div className='RagChatbotDetailPage__input-div--info'>{formEmailTemplateDefaultRedirectUrl}</div>
                  <div className='RagChatbotDetailPage__input-div--buttons'>
                    <div className='RagChatbotDetailPage__input-div--icon' onClick={handleCopyDefaultUrl}>
                      <CopyIcon />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className='RagChatbotDetailPage__row center'>
          <Button
            color="green"
            title="Save Configuration"
            onClick={handleSaveTalkjsConfig}
            loading={saving}
            disabled={saving || !supportsAdvancedFeatures}
            size="large"
          />
        </div>
      </div>
    </>
  );
};